import { memo, forwardRef, useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import isToday from '@sdk/utils/dateUtils/isToday';
import dateStringToDayDateString from '@sdk/utils/dateFormat/dateStringToDayDateString';
import useAuth from '@sdk/authentication/useAuth';
import Row from '@sdk/components/ui/spacing/Row';
import Text12 from '@sdk/components/ui/typography/Text12';

import FlexRow from './ui/FlexRow';
import type { FormattedDateString } from './types/FormattedDateString';
import Flex1 from './ui/Flex1';
import {
  IsSpecialDayType,
  GetDayTooltipType,
  OpenDayOptionsType,
  HasDayOptionsType,
  GoToDay,
  SetScheduleScrollOffsetType,
} from './types';
import ScheduleCellBgStyle, { ScheduleCellBgProps } from './components/ScheduleCellBgStyle';
import HeaderDayOptions from './components/HeaderDayOptions';

const BORDER_COLOR = 'rgba(0, 0, 0, 0.08)';
// remove this when we will have day mode in react
const GO_TO_DAY_DISABLED = true;

type HeaderRowProps = {
  rightOffset: number;
};
const HeaderRow = styled(FlexRow)<HeaderRowProps>`
  flex: 0;
  padding-right: ${(props): number => props.rightOffset || 0}px;
`;

const RelativeContainer = styled(Row)`
  position: relative;
`;

type HeaderCellProps = {
  leftBorder?: boolean;
} & ScheduleCellBgProps;
const HeaderCell = styled.div<HeaderCellProps>`
  ${ScheduleCellBgStyle}
  display: flex;
  flex: 1;
  border-right: 1px solid ${BORDER_COLOR};
  border-bottom: 1px solid ${BORDER_COLOR};
  align-items: center;
  justify-content: center;
  height: 31px;
`;

type Props = {
  currentPeriodDays: Array<FormattedDateString>;
  hasNameColumn: boolean;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
  openDayOptions: OpenDayOptionsType;
  getDayOptionsTooltip: GetDayTooltipType;
  hasDayOptions: HasDayOptionsType;
  goToDay: GoToDay;
};

type ForwardedAttrs = {
  setOffset: SetScheduleScrollOffsetType;
};

const Header = forwardRef<ForwardedAttrs, Props>(
  (
    {
      currentPeriodDays,
      hasNameColumn,
      isBusinessClosed,
      isHolidayPay,
      openDayOptions,
      getDayOptionsTooltip,
      hasDayOptions,
      goToDay,
    },
    ref
  ) => {
    const { currentUser } = useAuth();
    const [rightOffset, setRightOffset] = useState(0);

    useImperativeHandle(ref, () => ({
      setOffset: (offset: number): void => {
        if (rightOffset !== offset) {
          setRightOffset(offset);
        }
      },
    }));

    return (
      <HeaderRow rightOffset={rightOffset}>
        {hasNameColumn && (
          <Flex1>
            <HeaderCell></HeaderCell>
          </Flex1>
        )}
        {currentPeriodDays.map((day) => {
          const themeColor = isToday(day) ? ThemeColorType.primary : ThemeColorType.gray;

          return (
            <Flex1 key={day}>
              <HeaderCell
                isBusinessClosed={isBusinessClosed({ day })}
                isHolidayPay={isHolidayPay({ day })}>
                <RelativeContainer>
                  {!GO_TO_DAY_DISABLED && (
                    <Text12
                      as="a"
                      semiBold
                      uppercase
                      themeColor={themeColor}
                      onClick={(): void => goToDay({ day })}>
                      {dateStringToDayDateString(day)}
                    </Text12>
                  )}

                  {GO_TO_DAY_DISABLED && (
                    <Text12 semiBold uppercase themeColor={themeColor}>
                      {dateStringToDayDateString(day)}
                    </Text12>
                  )}

                  <HeaderDayOptions
                    hasDayOptions={hasDayOptions}
                    hoverComponent={HeaderCell}
                    readonly={!currentUser.isManager}
                    openDayOptions={openDayOptions}
                    getDayOptionsTooltip={getDayOptionsTooltip}
                    day={day}
                  />
                </RelativeContainer>
              </HeaderCell>
            </Flex1>
          );
        })}
      </HeaderRow>
    );
  }
);

Header.displayName = 'Header';

export default memo(Header);
